"use client";

import { createContext, forwardRef, useContext, useEffect, useRef, type ButtonHTMLAttributes, type KeyboardEvent } from "react";
import { Popover, type PopoverProps } from "@/components/Popover";

type MenuContextValue = { fechar: () => void };
const MenuContext = createContext<MenuContextValue | null>(null);

export type MenuProps = Omit<PopoverProps, "contentRole" | "contentRef" | "onContentKeyDown"> & {
  ariaLabel?: string;
};

export function Menu({ ariaLabel, children, ...props }: MenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props.open) return;
    menuRef.current?.querySelector<HTMLElement>("[role=\"menuitem\"]:not(:disabled):not([aria-disabled=\"true\"])")?.focus();
  }, [props.open]);

  function navegarMenu(event: KeyboardEvent<HTMLDivElement>) {
    const itens = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>("[role=\"menuitem\"]:not(:disabled):not([aria-disabled=\"true\"])") ?? [],
    );
    if (!["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(event.key)) return;
    event.preventDefault();
    const atual = itens.indexOf(document.activeElement as HTMLElement);
    if (event.key === "Enter" || event.key === " ") {
      itens[atual]?.click();
      return;
    }
    if (itens.length === 0) return;
    const proximo = event.key === "Home"
      ? 0
      : event.key === "End"
        ? itens.length - 1
        : (atual + (event.key === "ArrowDown" ? 1 : -1) + itens.length) % itens.length;
    itens[proximo]?.focus();
  }

  return (
    <Popover
      {...props}
      contentRole="menu"
      contentAriaLabel={ariaLabel}
      contentRef={menuRef}
      onContentKeyDown={navegarMenu}
      contentClassName={`overflow-hidden rounded-xl p-1.5 ${props.contentClassName ?? ""}`}
    >
      <MenuContext.Provider value={{ fechar: () => props.onOpenChange(false) }}>
        {children}
      </MenuContext.Provider>
    </Popover>
  );
}

export type MenuItemProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  onSelect?: () => void;
  closeOnSelect?: boolean;
};

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(function MenuItem(
  { onSelect, onClick, className = "", disabled, closeOnSelect = true, ...props },
  ref,
) {
  const menu = useContext(MenuContext);

  return (
    <button
      {...props}
      ref={ref}
      type="button"
      role="menuitem"
      tabIndex={-1}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          onSelect?.();
          if (closeOnSelect) menu?.fechar();
        }
      }}
      className={`flex w-full items-center rounded-lg text-left text-[var(--fz-floating-text)] outline-none transition-colors hover:bg-[var(--fz-floating-hover)] focus-visible:bg-[var(--fz-floating-hover)] focus-visible:ring-2 focus-visible:ring-[var(--fz-floating-focus-ring)] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    />
  );
});
